import clsx from 'clsx';
import { orderBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { tooltipClasses } from '../../components/grids/GridElement';
import Pager from '../../components/Pager';
import { useConfiguratorDispatch, useRootSelector } from '../../store';
import { Variant } from '../../types';
import { openSeeAll } from '../accordion/accordionSlice';
import { useGetProductQuery } from '../api/store/apiSlice';
import {
  selectIcon,
  selectProductTag,
  selectSlots,
  selectStyle,
  setIcon,
  setSlotField
} from '../item/store/itemSlice';
import { useIconsAndLabels } from './IconAndLabelHooks';

const DEFAULT_VARIANTS_TO_DISPLAY = 6;

export interface LabelVariantsProps {
  labelSlotId: string;
  showLabelName: boolean;
}

export default function LabelVariants({
  labelSlotId,
  showLabelName
}: LabelVariantsProps) {
  const dispatch = useConfiguratorDispatch();

  const [selected, setSelected] = useState<Variant>();

  const productId = useRootSelector(selectProductTag);
  const slots = useRootSelector(selectSlots);
  const thisSlot = slots[labelSlotId];
  const selectedStyle = useRootSelector(selectStyle);
  const selectedIcon = useRootSelector(selectIcon);
  const { data: product } = useGetProductQuery(productId);
  const { getDefaultIcon } = useIconsAndLabels();

  const label = useMemo(() => {
    const labelId = thisSlot.labelId;
    return product?.labels?.find(l => l.label_id === labelId);
  }, [product?.labels, thisSlot.labelId]);

  const variants = useMemo(() => {
    const unsortedVariants =
      label?.variants?.filter(v => v.style_id === selectedStyle?.style_id) ??
      [];
    return orderBy(unsortedVariants, v => v.system_name.toLocaleLowerCase());
  }, [label?.variants, selectedStyle?.style_id]);

  const totalPages =
    Math.ceil(variants?.length / DEFAULT_VARIANTS_TO_DISPLAY) ?? 0;
  const [page, setPage] = useState(0);
  const [variantSlice, setVariantSlice] = useState<Variant[]>([]);

  useEffect(() => {
    const start = page * DEFAULT_VARIANTS_TO_DISPLAY;
    const end = start + DEFAULT_VARIANTS_TO_DISPLAY;
    setVariantSlice(variants?.slice(start, end) ?? []);
  }, [variants, page]);

  useEffect(() => {
    const selectedVariant = variants.find(
      v => v.label_variant_id === thisSlot.variantId
    );
    setSelected(selectedVariant);
  }, [thisSlot.variantId, variants]);

  const thumbnailClasses = clsx(
    'm-px box-border h-[60px] w-[60px] rounded object-scale-down object-center'
  );

  const handleClick = (variant: Variant) => {
    dispatch(
      setSlotField({
        slotId: labelSlotId,
        field: 'variantId',
        value: variant.label_variant_id
      })
    );

    // Check if this variant supports icons (has any icons in the array)
    const variantSupportsIcons = variant.icons?.length > 0;

    // If variant supports icons, get the first available icon from the product
    const supportedVariantIcons = variantSupportsIcons
      ? product?.icons?.filter(
          icon => selectedStyle?.icon_ids?.includes(icon.icon_id)
        )
      : [];

    if (!variantSupportsIcons) {
      // Check if any other labels in the pack support icons
      const anyOtherLabelsSupportIcons = product?.label_slots?.some(slot => {
        // Skip the current slot
        if (slot.product_label_slot_id === labelSlotId) {
          return false;
        }

        const slotData = slots?.[slot.product_label_slot_id];
        if (!slotData?.labelId || !slotData?.variantId) {
          return false;
        }

        const slotLabel = product?.labels?.find(
          l => l.label_id === slotData.labelId
        );
        if (!slotLabel) {
          return false;
        }

        const slotVariant = slotLabel.variants?.find(
          v =>
            v.label_variant_id === slotData.variantId &&
            v.style_id === selectedStyle?.style_id
        );

        return (slotVariant?.icons?.length ?? 0) > 0;
      });

      // Only set default icon if no other labels support icons
      if (!anyOtherLabelsSupportIcons) {
        dispatch(setIcon(getDefaultIcon));
      }
      // If other labels support icons, keep the current icon selection
    } else {
      // Variant supports icons, check if we need to set a default
      if (supportedVariantIcons?.length) {
        if (!selectedIcon || selectedIcon?.display_name === 'Default Icon') {
          // No icon selected or current selection is default icon, set first available icon
          dispatch(setIcon(supportedVariantIcons[0]));
        } else {
          // Check if the currently selected icon is supported by this variant
          const isValidIcon = supportedVariantIcons.find(
            icon => icon.icon_id === selectedIcon?.icon_id
          );
          if (!isValidIcon) {
            // Selected icon is not supported by this variant, set first available icon
            dispatch(setIcon(supportedVariantIcons[0]));
          }
          // Selected icon is supported, keep current selection
        }
      }
    }
  };

  return (
    <>
      {showLabelName && label?.display_name ? (
        <p className="mt-2 font-semibold " style={{ color: '#555555' }}>
          {label?.display_name}
        </p>
      ) : null}
      <ol className="mt-2 grid grid-cols-6 items-center gap-2.5 pb-2.5">
        {variantSlice.map(variant => {
          return (
            <li
              key={variant.label_variant_id}
              className={clsx(
                'box-border h-[66px] w-[66px] rounded-md border-2 border-solid',
                variant == selected ? 'border-accent' : 'border-transparent'
              )}
            >
              <Tooltip id="variant-tooltip" className={tooltipClasses} />
              <img
                src={variant.thumbnail_path}
                className={thumbnailClasses}
                data-tooltip-id="variant-tooltip"
                data-tooltip-content={variant?.system_name || ''}
                data-tooltip-place="top"
                onClick={() => handleClick(variant)}
              />
            </li>
          );
        })}
      </ol>

      <div className="grid grid-cols-4 items-center pb-2.5">
        <span className="text-dark-gray truncate text-sm font-medium">
          {selected?.system_name}
        </span>
        <Pager
          size="small"
          totalPages={totalPages}
          currentPage={page}
          onBack={() => setPage(old => Math.max(0, old - 1))}
          onForward={() => setPage(old => Math.min(old + 1, totalPages - 1))}
        />
        <button
          onClick={() =>
            dispatch(openSeeAll({ entity: 'LabelVariants', labelSlotId }))
          }
          className="border-secondary-button-text bg-secondary-button text-secondary-button-text justify-self-end rounded-full border border-solid px-2.5 py-1.5 font-semibold transition-all hover:brightness-95"
        >
          See all
        </button>
      </div>
    </>
  );
}
